import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { energySystemApi, type EnergySystemData } from '@/services/api';

export default function EnergySystemPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [activeCase, setActiveCase] = useState(0);
  const [demoForm, setDemoForm] = useState({ name: '', phone: '', company: '', remarks: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const [data, setData] = useState<EnergySystemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    energySystemApi
      .get()
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : '加载失败');
        setLoading(false);
      });
  }, []);

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.phone || !demoForm.company) {
      setFormError('请填写必填项');
      return;
    }
    setFormError('');
    try {
      const res = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'demo',
          name: demoForm.name,
          phone: demoForm.phone,
          company: demoForm.company,
          remarks: demoForm.remarks,
        }),
      });
      if (!res.ok) throw new Error('submit failed');
      setFormSubmitted(true);
      setDemoForm({ name: '', phone: '', company: '', remarks: '' });
      setTimeout(() => setFormSubmitted(false), 4000);
    } catch {
      setFormError('提交失败，请稍后重试');
    }
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background-50">
        <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600 text-sm mt-4">{error || '加载失败'}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  const { features, advantages, caseStudies, faq, modules } = data;

  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50">

        {/* Hero Section */}
        <section className="relative w-full min-h-[650px] md:min-h-[750px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/energy-hero.jpg"
              alt="能碳管理系统"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="text-background-50">
                  <span className="inline-block px-3 py-1 bg-primary-500/80 text-background-50 text-xs font-medium rounded-full tracking-wide mb-5">
                    企业级智慧能源管理平台
                  </span>
                  <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                    让每一度电
                    <br />
                    都创造<span className="text-primary-300">看得见</span>的价值
                  </h1>
                  <p className="text-background-100/80 text-base md:text-lg mt-5 leading-relaxed max-w-lg">
                    覆盖电、水、气、热全能源品类，融合AI分析与碳排管理，
                    已服务500+企业实现平均节能15-30%，助力绿色低碳转型与合规达标。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mt-8">
                    <a
                      href="#demo-form"
                      className="group px-7 py-3.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-md hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
                    >
                      预约演示
                      <span className="w-6 h-6 rounded-full border border-foreground-900 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </a>
                    <Link
                      to="/news"
                      className="px-7 py-3.5 border border-background-50/40 text-background-50 text-sm font-semibold rounded-md hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
                    >
                      了解更多
                    </Link>
                  </div>
                </div>

                <div className="hidden lg:flex items-center justify-center">
                  <div className="relative w-full aspect-square max-w-md">
                    <div className="absolute inset-0 bg-primary-500/10 rounded-full blur-3xl"></div>
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: '15-30%', label: '平均节能率' },
                          { value: '500+', label: '服务企业' },
                          { value: '98.6%', label: '数据准确率' },
                          { value: '12月', label: '投资回收期' },
                        ].map((stat) => (
                          <div
                            key={stat.label}
                            className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-lg p-5 text-center"
                          >
                            <p className="text-3xl font-heading font-bold text-background-50">
                              {stat.value}
                            </p>
                            <p className="text-background-50/70 text-xs mt-1">{stat.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-background-50/60">
            <span className="text-xs tracking-widest uppercase">了解更多</span>
            <i className="ri-arrow-down-line text-lg animate-bounce"></i>
          </div>
        </section>

        {/* Core Features */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-primary-600 text-sm font-medium font-label tracking-wide uppercase">
                核心能力
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3">
                六大模块，全面覆盖能源管理需求
              </h2>
              <p className="text-foreground-500 text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                从实时监测到智能优化，从碳排放核算到合规报告，一站式解决企业能源管理全链路问题
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {features.map((feature, idx) => (
                <div
                  key={feature.id}
                  className="group bg-background-100 rounded-xl p-6 md:p-7 hover:bg-background-200/70 transition-colors duration-200 cursor-default"
                  style={{ animationDelay: `${idx * 80}ms` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-200 transition-colors">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2.5">
                    {feature.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Monitored Modules */}
        <section className="w-full py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                全能源品类覆盖
              </h2>
              <p className="text-foreground-500 text-sm mt-3">
                一个平台，统一管理所有能源类型
              </p>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6">
              {modules.map((mod) => (
                <div
                  key={mod.name}
                  className="flex flex-col items-center gap-3 p-4 rounded-lg hover:bg-background-50 transition-colors cursor-default"
                >
                  <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className={`${mod.icon} text-xl text-primary-600`}></i>
                  </div>
                  <span className="text-xs text-foreground-600 font-medium text-center whitespace-nowrap">
                    {mod.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages Stats */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-accent-600 text-sm font-medium font-label tracking-wide uppercase">
                  为什么选择我们
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3 leading-tight">
                  数据驱动的能源管理，
                  <br />
                  实实在在的降本增效
                </h2>
                <p className="text-foreground-500 text-base mt-4 leading-relaxed max-w-lg">
                  我们的系统不仅仅是数据采集工具，更是企业节能降碳的战略伙伴。
                  通过AI算法和行业对标模型，帮助企业发现被忽视的节能机会。
                </p>
                <div className="mt-8 grid grid-cols-2 gap-6">
                  {advantages.slice(0, 2).map((adv) => (
                    <div key={adv.id}>
                      <p className="text-4xl font-heading font-bold text-primary-600">{adv.value}</p>
                      <p className="text-foreground-900 text-sm font-semibold mt-1">{adv.label}</p>
                      <p className="text-foreground-400 text-xs mt-0.5">{adv.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {advantages.slice(2, 4).map((adv) => (
                  <div
                    key={adv.id}
                    className="bg-background-100 rounded-xl p-6 text-center"
                  >
                    <p className="text-4xl font-heading font-bold text-accent-600">{adv.value}</p>
                    <p className="text-foreground-900 text-sm font-semibold mt-2">{adv.label}</p>
                    <p className="text-foreground-400 text-xs mt-1">{adv.description}</p>
                  </div>
                ))}
                <div className="bg-primary-100 rounded-xl p-6 text-center col-span-2">
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    <i className="ri-double-quotes-l text-primary-400 mr-1"></i>
                    能碳管理系统是我们数字化转型中最具回报率的投资之一，部署半年就收回了全部成本。
                  </p>
                  <p className="text-foreground-400 text-xs mt-2">
                    —— 某化工集团能源管理部负责人
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="w-full py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="text-accent-600 text-sm font-medium font-label tracking-wide uppercase">
                客户案例
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-3">
                他们已通过能耗管理实现降本增效
              </h2>
            </div>

            {caseStudies.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-8 justify-center">
                  {caseStudies.map((cs, idx) => (
                    <button
                      key={cs.id}
                      onClick={() => setActiveCase(idx)}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeCase === idx
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:bg-background-200/70'
                      }`}
                    >
                      {cs.industry}
                    </button>
                  ))}
                </div>

                <div className="bg-background-50 rounded-2xl overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="h-[280px] md:h-[380px]">
                      <img
                        src={`/images/${caseStudies[activeCase].imageSeq}.jpg`}
                        alt={caseStudies[activeCase].company}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded">
                          {caseStudies[activeCase].industry}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                        {caseStudies[activeCase].company}
                      </h3>

                      <div className="mt-5 space-y-3">
                        <div className="flex items-start gap-2.5">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-foreground-200 flex items-center justify-center mt-0.5">
                            <span className="text-foreground-500 text-[10px] font-bold">前</span>
                          </span>
                          <p className="text-foreground-500 text-sm leading-relaxed">
                            {caseStudies[activeCase].before}
                          </p>
                        </div>
                        <div className="flex items-start gap-2.5">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center mt-0.5">
                            <span className="text-background-50 text-[10px] font-bold">后</span>
                          </span>
                          <p className="text-foreground-700 text-sm leading-relaxed">
                            {caseStudies[activeCase].after}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 pt-5 border-t border-background-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-money-dollar-circle-line text-accent-600 text-lg"></i>
                          <span className="text-accent-700 text-base font-bold font-heading">
                            {caseStudies[activeCase].savings}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* FAQ + CTA Form */}
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
              {/* FAQ */}
              <div>
                <span className="text-primary-600 text-sm font-medium font-label tracking-wide uppercase">
                  常见问题
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-3 mb-8">
                  关于能碳管理系统
                </h2>
                <div className="space-y-3">
                  {faq.map((item, idx) => (
                    <div
                      key={idx}
                      className="border border-background-200 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                      >
                        <span className="text-foreground-800 text-sm font-medium pr-4">{item.q}</span>
                        <i
                          className={openFAQ === idx ? 'ri-arrow-up-s-line text-foreground-400 flex-shrink-0 transition-transform duration-200' : 'ri-arrow-down-s-line text-foreground-400 flex-shrink-0 transition-transform duration-200'}
                        ></i>
                      </button>
                      {openFAQ === idx && (
                        <div className="px-4 pb-4">
                          <p className="text-foreground-500 text-sm leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Demo Form */}
              <div id="demo-form" className="bg-background-100 rounded-2xl p-8 md:p-10">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2">
                  预约免费演示
                </h2>
                <p className="text-foreground-500 text-sm mb-7">
                  留下联系方式，我们的工程师将在1个工作日内与您联系
                </p>

                {formSubmitted ? (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <p className="text-primary-800 text-base font-semibold">提交成功！</p>
                    <p className="text-primary-600 text-sm mt-1">我们将尽快与您联系</p>
                  </div>
                ) : (
                  <form id="demo-request" onSubmit={handleDemoSubmit} className="space-y-4">
                    <div>
                      <label className="block text-foreground-700 text-sm mb-1.5">
                        联系人 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={demoForm.name}
                        onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                        className="w-full bg-background-50 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-700 text-sm mb-1.5">
                        手机号 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                        className="w-full bg-background-50 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的手机号"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-700 text-sm mb-1.5">
                        企业名称 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={demoForm.company}
                        onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                        className="w-full bg-background-50 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的企业名称"
                      />
                    </div>
                    <div>
                      <label className="block text-foreground-700 text-sm mb-1.5">备注</label>
                      <textarea
                        name="remarks"
                        value={demoForm.remarks}
                        onChange={(e) => setDemoForm({ ...demoForm, remarks: e.target.value })}
                        maxLength={500}
                        className="w-full bg-background-50 border border-background-300 rounded-md px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none h-24"
                        placeholder="请简要描述您的能源管理需求（选填）"
                      />
                    </div>
                    {formError && (
                      <p className="text-accent-600 text-xs">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交预约
                    </button>
                    <p className="text-foreground-400 text-xs text-center">
                      提交即表示您同意我们的
                      <Link to="/agreement/privacy" className="text-primary-600 underline cursor-pointer ml-0.5">隐私政策</Link>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full py-14 md:py-20 bg-foreground-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              准备好开启智能能源管理了吗？
            </h2>
            <p className="text-foreground-400 text-base mt-4 max-w-xl mx-auto">
              加入500+企业的行列，用数据驱动能源效率提升，实现降本增效与绿色转型
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <a
                href="#demo-form"
                className="px-8 py-3.5 bg-background-50 text-foreground-900 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即预约演示
              </a>
              <Link
                to="/news"
                className="px-8 py-3.5 border border-background-50/30 text-background-50 text-sm font-semibold rounded-md hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多行业资讯
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}